"use client";

import React from "react";
import { Phone, Mail, MapPin, Send, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-accent/30 p-8 text-center shadow-sm">
        <div className="mb-4 flex size-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Send className="size-6" />
        </div>
        <h3 className="font-default mb-2 text-xl font-semibold text-foreground">Message Sent</h3>
        <p className="mb-6 text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline">
          Send Another Message
        </Button>
      </div>;
  }
  const inputStyles = "border-x-0 border-t-0 border-b-2 border-b-border rounded-md bg-transparent px-2 shadow-sm focus-visible:border-b-primary focus-visible:ring-0 md:text-base";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[0-9-+() ]{10,15}$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(330) 203-1488" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground">Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="john@example.com" className={inputStyles} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Message must be at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground">How can we help?</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your cleaning needs..." className={`min-h-[120px] resize-y ${inputStyles}`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} size="lg" className="w-full md:w-auto">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Sending...
          </> : <>
            Send Message
            <Send className="ml-2 size-4" />
          </>}
      </Button>
    </Form>;
}
export default function ContactInquiry() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: formRef,
    isVisible: isFormVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="contactdetailswithinquiryform" className="relative overflow-hidden bg-background py-20 md:py-32">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03] dark:opacity-[0.05]" style={{
      backgroundImage: `
            linear-gradient(var(--primary) 1px, transparent 1px),
            linear-gradient(90deg, var(--primary) 1px, transparent 1px)
          `,
      backgroundSize: "40px 40px"
    }} aria-hidden="true" />

      <div className="container relative mx-auto max-w-7xl px-6">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:gap-24">
          
          {}
          <div ref={headerRef} className={`col-span-1 flex flex-col justify-center lg:col-span-5 transition-all duration-700 ease-out ${isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
            <div className="mb-8 inline-flex items-center rounded-md bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
              Get in Touch
            </div>
            <h2 className="font-default mb-6 text-3xl font-bold tracking-tight text-foreground md:text-4xl lg:text-5xl">
              Let's discuss your cleaning needs.
            </h2>
            <p className="mb-10 text-lg text-muted-foreground">
              Whether you need a personalized quote for your home, or a walkthrough for your commercial space, our network of professionals is ready to help.
            </p>

            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary shadow-sm">
                  <Phone className="size-6" />
                </div>
                <div>
                  <h3 className="font-default mb-1 text-lg font-semibold text-foreground">Call Us</h3>
                  <Link to="tel:330-203-1488" className="text-lg text-muted-foreground transition-colors hover:text-primary">
                    (330) 203-1488
                  </Link>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary shadow-sm">
                  <Mail className="size-6" />
                </div>
                <div>
                  <h3 className="font-default mb-1 text-lg font-semibold text-foreground">Email Us</h3>
                  <Link to="mailto:info@summitcleanersohio.com" className="text-lg text-muted-foreground transition-colors hover:text-primary">
                    info@summitcleanersohio.com
                  </Link>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary shadow-sm">
                  <MapPin className="size-6" />
                </div>
                <div>
                  <h3 className="font-default mb-1 text-lg font-semibold text-foreground">Service Area</h3>
                  <p className="text-lg text-muted-foreground">
                    Akron, Barberton, Massillon, and surrounding Northeast Ohio areas.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {}
          <div ref={formRef} className={`col-span-1 lg:col-span-7 transition-all duration-700 delay-200 ease-out ${isFormVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
            <div className="rounded-xl border border-border bg-card p-8 shadow-lg md:p-12">
              <h3 className="font-default mb-8 text-2xl font-bold text-foreground">
                Send an Inquiry
              </h3>
              <FormProvider formId="contact-inquiry-form" sectionName="ContactDetailsWithInquiryForm" defaultValues={{
              name: "",
              email: "",
              phone: "",
              message: ""
            }} submitText="Send Message" successMessage="Thank you for reaching out! We will get back to you shortly.">
                <InquiryForm />
              </FormProvider>
            </div>
          </div>
          
        </div>
      </div>
    </section>;
}